import {
  Button,
  Card,
  Center,
  Flex,
  Group,
  Pagination,
  Stack,
  Text
} from '@mantine/core'
import { useInterval } from '@mantine/hooks'
import { IconRefresh } from '@tabler/icons-react'
import { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { useGetDeviceList } from '@/queries/deviceQueries'
import { getPageCount, getPageOffset } from '@/utils/pagination'
import { ErrorWithReload } from '../ui-shared/ErrorWithReload/ErrorWithReload'
import { PlatformAlert } from '../ui-shared/PlatformAlert/PlatformAlert'
import { useTableState } from '../ui-shared/Tables/useTableState'
import { VlmDeviceCard } from './VlmDeviceCard'
import { VlmDeviceCardSkeleton } from './VlmDeviceCardSkeleton'

const PAGE_SIZE = 5
const SKELETON_COUNT = 3
const POLL_INTERVAL = 10000

export const VlmHardwarePage = () => {
  const [tableState, setTableState] = useTableState({ activePage: 1 })
  const { activePage } = tableState

  const { data, isLoading, isFetching, isError, refetch } = useGetDeviceList(
    {
      offset: getPageOffset(activePage, PAGE_SIZE),
      limit: PAGE_SIZE,
      ordering: 'name'
    },
    { keepPreviousData: true }
  )

  const devices = data?.results ?? []
  const totalPageCount = getPageCount(data?.count ?? 0, PAGE_SIZE)

  useEffect(() => {
    if (totalPageCount > 0 && activePage > totalPageCount) {
      setTableState({ activePage: totalPageCount })
    }
  }, [activePage, totalPageCount, setTableState])

  const { start, stop, active } = useInterval(
    () => void refetch(),
    POLL_INTERVAL
  )

  useEffect(() => {
    if (data && !active) {
      start()
    }
  }, [data, active, start])

  // Stop polling when component unmounts
  useEffect(() => {
    return stop
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const renderContent = () => {
    if (!isLoading && devices.length === 0) {
      return (
        <Card withBorder>
          <Center h={160}>
            <Text c="dimmed">
              <FormattedMessage id="vlm.hardware.empty" />
            </Text>
          </Center>
        </Card>
      )
    }

    return (
      <Stack>
        {isLoading
          ? Array.from({ length: SKELETON_COUNT }).map((_, i) => (
              <VlmDeviceCardSkeleton key={i} />
            ))
          : devices.map((device) => (
              <VlmDeviceCard key={device.id} device={device} />
            ))}
      </Stack>
    )
  }

  if (isError) {
    return (
      <ErrorWithReload onReload={() => void refetch()}>
        <FormattedMessage id="devices.data.error" />
      </ErrorWithReload>
    )
  }

  return (
    <>
      <PlatformAlert
        variant="warning"
        title={<FormattedMessage id="vlm.hardware.requirements.title" />}
        mb="lg"
      >
        <FormattedMessage id="vlm.hardware.requirements" />
      </PlatformAlert>

      <Group justify="end" mb="xs">
        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconRefresh size={14} />}
          loading={isFetching}
          onClick={() => void refetch()}
        >
          <FormattedMessage id="refresh" />
        </Button>
      </Group>

      {renderContent()}

      <Flex justify="end" mt="lg">
        <Pagination
          size="sm"
          total={totalPageCount}
          value={activePage}
          onChange={(value) => setTableState({ activePage: value })}
        />
      </Flex>
    </>
  )
}
